import {Survey, SurveyElement, SurveyPage} from 'core/SurveyModels'
import {v4 as UUIDv4} from 'uuid'
import {SurveyValidatorService} from 'core/builder/SurveyValidatorService'
import {SurveyValidator} from 'core/builder/validators/SurveyValidator'

export class SurveyBuilder {
  private version = UUIDv4()
  private survey: Survey = {
    name: UUIDv4(),
    autoAdvance: false,
    pages: []
  }
  private static instance?: SurveyBuilder
  private validator = new SurveyValidatorService()

  public findPageIndex(pageName: string) {
    return this.survey.pages.findIndex((page) => page.name === pageName)
  }

  public findElementIndex(
    forPage: string,
    elementName: string
  ): [number, number] {
    const pageIndex = this.findPageIndex(forPage)
    if (pageIndex === -1) {
      return [-1, -1]
    }
    return [
      pageIndex,
      this.survey.pages[pageIndex]!.elements.findIndex(
        (element) => element.name === elementName
      )
    ]
  }

  getDisplayPageName(name: string, index?: number) {
    if (name.length <= 15) {
      return name
    }
    const pageIndex = index ?? this.findPageIndex(name) + 1
    return `Page ${pageIndex}`
  }

  getPageByElement(name: string) {
    return this.survey.pages.find((page) =>
      page.elements.find((element) => element.name === name)
    )
  }

  getPage(name: string) {
    return this.survey.pages.find((page) => page.name === name)
  }

  getElement(pageName: string, elementName: string) {
    return this.getPage(pageName)?.elements.find(
      (el) => el.name === elementName
    )
  }

  createPage(page: SurveyPage) {
    this.survey.pages.push(page)
    this.updateVersion()
    return page
  }

  removePage(pageName: string) {
    const index = this.findPageIndex(pageName)
    if (index !== -1) {
      this.survey.pages.splice(index, 1)
      this.updateVersion()
    }
  }

  createElement(forPageName: string, element: SurveyElement) {
    const index = this.findPageIndex(forPageName)
    if (index !== -1) {
      this.survey.pages[index]!.elements.push(element)
      this.updateVersion()
      return element
    }
    throw new Error('Survey: Page not found')
  }

  removeElement(forPageName: string, elementName: string) {
    const [pageIndex, elementIndex] = this.findElementIndex(
      forPageName,
      elementName
    )
    if (pageIndex !== -1 && elementIndex !== -1) {
      this.survey.pages[pageIndex]!.elements.splice(elementIndex, 1)
      this.updateVersion()
    }
  }

  updatePageAttribute(pageName: string, attributes: Partial<SurveyPage>) {
    const pageIndex = this.findPageIndex(pageName)
    if (pageIndex !== -1) {
      this.survey.pages[pageIndex] = {
        ...this.survey.pages[pageIndex],
        ...attributes
      }
    }
    this.updateVersion()
    return this.survey.pages[pageIndex]
  }

  updateElementAttribute(
    elementName: string,
    forPageName: string,
    attributes: Partial<SurveyElement>
  ) {
    const [pageIndex, elementIndex] = this.findElementIndex(
      forPageName,
      elementName
    )
    if (pageIndex !== -1 && elementIndex !== -1) {
      this.survey.pages[pageIndex].elements[elementIndex] = {
        ...this.survey.pages[pageIndex].elements[elementIndex],
        ...attributes
      }
      this.updateVersion()
      return this.survey.pages[pageIndex].elements[elementIndex]
    }
  }

  updateSurveyAttribute(attribute: Partial<Omit<Survey, 'pages'>>) {
    this.survey = {...this.survey, ...attribute}
    return this.survey
  }

  updateElementName(elementName: string, pageName: string, name: string) {
    this.survey.pages.forEach((page) =>
      page.elements.forEach((element) => {
        if (
          element.condition?.target.page === pageName &&
          element.condition.target.element === elementName
        ) {
          element.condition.target.element = name
        }
        page.branches?.forEach((branch) => {
          branch.conditions?.forEach((condition) => {
            if (
              page.name === pageName &&
              condition.elementName === elementName
            ) {
              condition.elementName = name
            }
          })
        })
      })
    )
    return this.updateElementAttribute(elementName, pageName, {name: name})
  }

  getRelatedElements(pageName: string, elementName: string) {
    const relations: {page: SurveyPage; element: SurveyElement}[] = []

    this.survey.pages.forEach((page) =>
      page.elements.forEach((element) => {
        if (
          element.condition?.target?.page === pageName &&
          element.condition?.target?.element === elementName
        ) {
          relations.push({page: page, element: element})
        }
      })
    )

    return relations
  }

  reorderPage(sourceIndex: number, targetIndex: number) {
    const page = this.survey.pages.splice(sourceIndex, 1)
    this.survey.pages.splice(targetIndex, 0, ...page)
  }

  pages() {
    return this.survey.pages
  }

  export() {
    return this.survey //this.deepCopy(this.survey)
  }

  getVersion() {
    return this.version
  }

  async validate(appendTemplate?: Survey[]) {
    const survey: Survey = {
      ...this.survey,
      pages: [
        ...this.survey.pages,
        ...(appendTemplate ?? []).flatMap((t) => t.pages)
      ]
    }
    return await this.validator.validate(survey)
  }

  async customValidation(validators: SurveyValidator[]) {
    return this.validator.custom(validators)
  }

  freeShared() {
    delete SurveyBuilder.instance
  }

  static free() {
    delete SurveyBuilder.instance
  }

  public deepCopy<T>(instance: T): T {
    if (instance instanceof Array) {
      const cloneArr = [] as unknown[]
      ;(instance as unknown[]).forEach((value) => {
        cloneArr.push(value)
      })
      return cloneArr.map((value: unknown) =>
        this.deepCopy<unknown>(value)
      ) as T
    }
    if (instance instanceof Object) {
      const copyInstance = {
        ...(instance as {[key: string]: unknown})
      } as {[key: string]: unknown}
      for (const attr in instance) {
        if (Object.prototype.hasOwnProperty.call(instance as object, attr))
          copyInstance[attr] = this.deepCopy<unknown>(
            (instance as Record<string, unknown>)[attr]
          )
      }
      return copyInstance as T
    }
    return instance
  }

  private updateVersion() {
    this.version = UUIDv4()
  }

  static createDefaultElement(): SurveyElement {
    return {
      type: 'text',
      name: UUIDv4(),
      title: {en: 'Question'},
      required: true,
      errorText: {en: 'Please answer the following question'}
    }
  }

  static tryExtendSurvey(
    survey: Survey,
    insertPagesAtIndex: number,
    pages: SurveyPage[]
  ): Survey {
    const copy = {...survey}
    const hasDuplicatedPage = pages.some((page) =>
      survey.pages.some((sp) => sp.name === page.name)
    )
    if (hasDuplicatedPage) {
      throw new Error('Page already exists')
    }
    copy.pages.splice(insertPagesAtIndex, 0, ...pages)
    return copy
  }

  static shared(survey?: Survey) {
    if (!SurveyBuilder.instance) {
      const builder = new SurveyBuilder()
      if (!survey) {
        builder.createPage({name: UUIDv4(), elements: []})
        builder.createElement(UUIDv4(), {
          type: 'text',
          name: UUIDv4(),
          title: {en: 'Question'}
        })
      } else {
        builder.survey = builder.deepCopy(survey)
      }
      SurveyBuilder.instance = builder
    }
    return SurveyBuilder.instance
  }
}
